import type { AvocationalHobby } from '../stores/avocationalStore';
import type { Task, Habit } from '../types';
import { taskEarnedPoints } from './taskPoints';

/**
 * Avocational hobby XP (the 4-layer ledger: User ← Domain ← Path ← Hobby).
 *
 * A hobby is the trainable unit. Its EARNED XP = its persisted play-session XP
 * (`totalXp`, written by `logActivity`) PLUS the point value of every avocational
 * task/habit tagged to it (`hobbyId`). Actions FEED the hobby (the inverse of
 * Intellectual's inert `subjectId` tags) — this is what makes "do an action →
 * progress the hobby → progress the path → progress the domain" real.
 *
 * CREDIT-ONCE: because hobby-tagged actions are counted here, `useUserStats` must
 * EXCLUDE those same hobby-tagged avocational tasks/habits from the generic
 * category sum, and add `computeAvocationalDomainXP` exactly once instead (mirrors
 * `computeIntellectualLearningXP`). Untagged avocational actions keep crediting via
 * the generic category. Net XP is neutral — a tagged action's value just moves from
 * the category ledger into its hobby's ledger.
 *
 * Toggle-safe: a task contributes only when it has a `pointRecord` (set on
 * completion, cleared on un-complete) and a habit contributes the sum of its
 * completion points — the exact same accessors `useUserStats` uses, so the hobby
 * and the domain always agree.
 */

/** The point value a completed task has banked, including its completed subtasks —
 * mirrors `useUserStats` EXACTLY (subtask points count only when the parent has a
 * pointRecord), so excluding a hobby-tagged task from the generic sum and re-adding
 * it here is XP-neutral. An incomplete parent contributes 0 even if a subtask is done. */
function taskPoints(t: Task): number {
  // Includes XP kept from replayed folder rounds — the same accessor as `useUserStats`.
  return taskEarnedPoints(t);
}

/** The total points a habit has banked across its completions. */
function habitPoints(h: Habit): number {
  return h.completions?.reduce((sum, c) => sum + c.points, 0) || 0;
}

/**
 * A single hobby's earned XP = play-session XP + points of the avocational
 * tasks/habits tagged to it. Pass the avocational-category tasks/habits.
 */
export function hobbyEarnedXp(
  hobby: AvocationalHobby,
  avocTasks: Task[],
  avocHabits: Habit[],
): number {
  const playXp = Math.max(0, hobby.totalXp || 0);
  const fromTasks = avocTasks.reduce((sum, t) => (t.hobbyId === hobby.id ? sum + taskPoints(t) : sum), 0);
  const fromHabits = avocHabits.reduce((sum, h) => (h.hobbyId === hobby.id ? sum + habitPoints(h) : sum), 0);
  return playXp + fromTasks + fromHabits;
}

/**
 * The Avocational domain's DERIVED XP — Σ earned XP over ACTIVE hobbies. This is
 * the single value `useUserStats` adds to credit the domain once (Σ paths = Σ
 * hobbies). Archived hobbies don't count. Pass the avocational-category tasks and
 * habits (the same collections `useUserStats` excludes from its generic sums).
 */
export function computeAvocationalDomainXP(
  hobbies: AvocationalHobby[],
  avocTasks: Task[],
  avocHabits: Habit[],
): number {
  return hobbies
    .filter(h => h.isActive)
    .reduce((sum, h) => sum + hobbyEarnedXp(h, avocTasks, avocHabits), 0);
}
